import { Routes } from '@angular/router';
import { resolveTitleFor, Route, RouteFragment, RouteParam } from '@web/common';
import { HasPermissionGuard, OnlyLoggedGuard } from '@web/shared/module/auth';
import { AdminMultimediaDetailsEventBusService } from '@web/shared/module/multimedia';
import { AdminBrandEventBusService } from '@web/features/brand';
import {
  WarehouseLocalizationEventBusService,
  WarehouseProductEventBusService,
  WarehouseReceiptEventBusService,
  WarehouseReleaseEventBusService,
  WarehouseRestockEventBusService,
} from '@web/shared/module/warehouse';
import { AdminDictionaryEventBusService } from '@web/shared/module/dictionary';
import { UserEventBusService } from '@web/features/user';
import { AdminBannerEventBusService } from '@web/features/banner';
import { AdminCosmeticEventBusService } from '@web/features/cosmetic';
import { AdminProductEventBusService } from '@web/features/product';
import {
  AdminPaymentMethodEventBusService,
  AdminShippingMethodEventBusService,
} from '@web/features/shop';
import { AdminCompanyEventBusService } from '@web/features/company';
import {
  AdminBoxEventBusService,
  AdminBoxSaleEventBusService,
} from '@web/features/box';
import { AdminBackgroundTaskEventBusService } from '@web/features/system';

export const routes: Routes = [
  {
    path: RouteFragment.Empty,
    canActivate: [OnlyLoggedGuard],
    loadComponent: () =>
      import('./layout/layout.component').then(
        ({ LayoutComponent }) => LayoutComponent,
      ),
    children: [
      {
        path: RouteFragment.Empty,
        pathMatch: 'full',
        redirectTo: RouteFragment.Dashboard,
      },
      {
        path: RouteFragment.Dashboard,
        title: resolveTitleFor(Route.Dashboard),
        loadComponent: () =>
          import('./pages/dashboard').then((c) => c.DashboardPageComponent),
      },
      {
        path: RouteFragment.Dictionaries,
        title: resolveTitleFor(Route.DictionaryList),
        canActivate: [HasPermissionGuard(Route.DictionaryList)],
        children: [
          {
            path: RouteFragment.Empty,
            redirectTo: RouteFragment.BoxCategory,
            pathMatch: 'full',
          },
          {
            path: RouteFragment.BoxCategory,
            title: resolveTitleFor(Route.DictionaryBoxCategoryList),
            canActivate: [HasPermissionGuard(Route.DictionaryBoxCategoryList)],
            providers: [AdminDictionaryEventBusService],
            loadComponent: () =>
              import('./pages/dictionary').then(
                (c) => c.DictionaryBoxCategoryListPageComponent,
              ),
            children: [
              {
                path: RouteFragment.Create,
                title: resolveTitleFor(Route.DictionaryBoxCategoryCreate),
                canActivate: [
                  HasPermissionGuard(Route.DictionaryBoxCategoryCreate),
                ],
                loadComponent: () =>
                  import('./pages/dictionary').then(
                    (c) => c.DictionaryBoxCategoryCreatePageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
                title: resolveTitleFor(Route.DictionaryBoxCategoryEdit),
                canActivate: [
                  HasPermissionGuard(Route.DictionaryBoxCategoryEdit),
                ],
                loadComponent: () =>
                  import('./pages/dictionary').then(
                    (c) => c.DictionaryBoxCategoryEditPageComponent,
                  ),
              },
            ],
          },
          {
            path: RouteFragment.Country,
            title: resolveTitleFor(Route.DictionaryCountryList),
            canActivate: [HasPermissionGuard(Route.DictionaryCountryList)],
            providers: [AdminDictionaryEventBusService],
            loadComponent: () =>
              import('./pages/dictionary').then(
                (c) => c.DictionaryCountryListPageComponent,
              ),
            children: [
              {
                path: RouteFragment.Create,
                title: resolveTitleFor(Route.DictionaryCountryCreate),
                canActivate: [
                  HasPermissionGuard(Route.DictionaryCountryCreate),
                ],
                loadComponent: () =>
                  import('./pages/dictionary').then(
                    (c) => c.DictionaryCountryCreatePageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
                title: resolveTitleFor(Route.DictionaryCountryEdit),
                canActivate: [HasPermissionGuard(Route.DictionaryCountryEdit)],
                loadComponent: () =>
                  import('./pages/dictionary').then(
                    (c) => c.DictionaryCountryEditPageComponent,
                  ),
              },
            ],
          },
          {
            path: RouteFragment.ProductAttribute,
            title: resolveTitleFor(Route.DictionaryProductAttributeList),
            canActivate: [
              HasPermissionGuard(Route.DictionaryProductAttributeList),
            ],
            providers: [AdminDictionaryEventBusService],
            loadComponent: () =>
              import('./pages/dictionary').then(
                (c) => c.DictionaryProductAttributeListPageComponent,
              ),
            children: [
              {
                path: RouteFragment.Create,
                title: resolveTitleFor(Route.DictionaryProductAttributeCreate),
                canActivate: [
                  HasPermissionGuard(Route.DictionaryProductAttributeCreate),
                ],
                loadComponent: () =>
                  import('./pages/dictionary').then(
                    (c) => c.DictionaryProductAttributeCreatePageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
                title: resolveTitleFor(Route.DictionaryProductAttributeEdit),
                canActivate: [
                  HasPermissionGuard(Route.DictionaryProductAttributeEdit),
                ],
                loadComponent: () =>
                  import('./pages/dictionary').then(
                    (c) => c.DictionaryProductAttributeEditPageComponent,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: RouteFragment.Multimedia,
        title: resolveTitleFor(Route.Multimedia),
        canActivate: [HasPermissionGuard(Route.Multimedia)],
        children: [
          {
            path: RouteFragment.Empty,
            pathMatch: 'full',
            redirectTo: RouteFragment.Grid,
          },
          {
            path: RouteFragment.Upload,
            title: resolveTitleFor(Route.MultimediaUpload),
            canActivate: [HasPermissionGuard(Route.MultimediaUpload)],
            loadComponent: () =>
              import('./pages/multimedia').then(
                (c) => c.MultimediaUploadPageComponent,
              ),
          },
          {
            path: RouteFragment.Grid,
            title: resolveTitleFor(Route.MultimediaGridView),
            canActivate: [HasPermissionGuard(Route.MultimediaGridView)],
            providers: [AdminMultimediaDetailsEventBusService],
            loadComponent: () =>
              import('./pages/multimedia').then(
                (c) => c.MultimediaGridViewPageComponent,
              ),
            children: [
              {
                path: `:${RouteParam.Guid}`,
                canActivate: [
                  HasPermissionGuard(Route.MultimediaDetailsFromGrid),
                ],
                loadComponent: () =>
                  import('./pages/multimedia').then(
                    (c) => c.MultimediaDetailsPageComponent,
                  ),
              },
            ],
          },
          {
            path: RouteFragment.List,
            title: resolveTitleFor(Route.MultimediaListView),
            canActivate: [HasPermissionGuard(Route.MultimediaListView)],
            providers: [AdminMultimediaDetailsEventBusService],
            loadComponent: () =>
              import('./pages/multimedia').then(
                (c) => c.MultimediaListViewPageComponent,
              ),
            children: [
              {
                path: `:${RouteParam.Guid}`,
                canActivate: [
                  HasPermissionGuard(Route.MultimediaDetailsFromList),
                ],
                loadComponent: () =>
                  import('./pages/multimedia').then(
                    (c) => c.MultimediaDetailsPageComponent,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: RouteFragment.Companies,
        title: resolveTitleFor(Route.Companies),
        canActivate: [HasPermissionGuard(Route.Companies)],
        providers: [AdminCompanyEventBusService],
        loadComponent: () =>
          import('./pages/company').then((c) => c.CompanyListPageComponent),
        children: [
          {
            path: RouteFragment.Create,
            title: resolveTitleFor(Route.CompanyCreate),
            canActivate: [HasPermissionGuard(Route.CompanyCreate)],
            loadComponent: () =>
              import('./pages/company').then(
                (c) => c.CompanyCreatePageComponent,
              ),
          },
          {
            path: `:${RouteParam.Guid}`,
            title: resolveTitleFor(Route.CompanyDetails),
            canActivate: [HasPermissionGuard(Route.CompanyDetails)],
            loadComponent: () =>
              import('./pages/company').then(
                (c) => c.CompanyDetailsPageComponent,
              ),
          },
          {
            path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
            title: resolveTitleFor(Route.CompanyEdit),
            canActivate: [HasPermissionGuard(Route.CompanyEdit)],
            loadComponent: () =>
              import('./pages/company').then((c) => c.CompanyEditPageComponent),
          },
        ],
      },
      {
        path: RouteFragment.Pages,
        title: resolveTitleFor(Route.Pages),
        canActivate: [HasPermissionGuard(Route.Pages)],
        loadComponent: () =>
          import('./pages/pages').then((c) => c.PageListPageComponent),
        children: [
          {
            path: RouteFragment.Create,
            title: resolveTitleFor(Route.PageCreate),
            canActivate: [HasPermissionGuard(Route.PageCreate)],
            loadComponent: () =>
              import('./pages/pages').then((c) => c.PageCreatePageComponent),
          },
          {
            path: `:${RouteParam.Guid}`,
            title: resolveTitleFor(Route.PageDetails),
            canActivate: [HasPermissionGuard(Route.PageDetails)],
            loadComponent: () =>
              import('./pages/pages').then((c) => c.PageDetailsPageComponent),
          },
          {
            path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
            title: resolveTitleFor(Route.PageEdit),
            canActivate: [HasPermissionGuard(Route.PageEdit)],
            loadComponent: () =>
              import('./pages/pages').then((c) => c.PageEditPageComponent),
          },
        ],
      },
      {
        path: RouteFragment.FaqCategory,
        title: resolveTitleFor(Route.FaqCategories),
        canActivate: [HasPermissionGuard(Route.FaqCategories)],
        loadComponent: () =>
          import('./pages/faq-category').then((c) => c.FaqCategoryListPageComponent),
        children: [
          {
            path: RouteFragment.Create,
            title: resolveTitleFor(Route.FaqCategoryCreate),
            canActivate: [HasPermissionGuard(Route.FaqCategoryCreate)],
            loadComponent: () =>
              import('./pages/faq-category').then((c) => c.FaqCategoryCreatePageComponent),
          },
          {
            path: `:${RouteParam.Guid}`,
            title: resolveTitleFor(Route.FaqCategoryDetails),
            canActivate: [HasPermissionGuard(Route.FaqCategoryDetails)],
            loadComponent: () =>
              import('./pages/faq-category').then((c) => c.FaqCategoryDetailsPageComponent),
          },
          {
            path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
            title: resolveTitleFor(Route.FaqCategoryEdit),
            canActivate: [HasPermissionGuard(Route.FaqCategoryEdit)],
            loadComponent: () =>
              import('./pages/faq-category').then((c) => c.FaqCategoryEditPageComponent),
          },
        ],
      },
      {
        path: RouteFragment.Users,
        title: resolveTitleFor(Route.Users),
        canActivate: [HasPermissionGuard(Route.Users)],
        providers: [UserEventBusService],
        loadComponent: () =>
          import('./pages/user').then((c) => c.UserListPageComponent),
        children: [
          {
            path: RouteFragment.Create,
            title: resolveTitleFor(Route.UserCreate),
            canActivate: [HasPermissionGuard(Route.UserCreate)],
            loadComponent: () =>
              import('./pages/user').then((c) => c.UserCreatePageComponent),
          },
          {
            path: `:${RouteParam.Guid}`,
            title: resolveTitleFor(Route.UserDetails),
            canActivate: [HasPermissionGuard(Route.UserDetails)],
            loadComponent: () =>
              import('./pages/user').then((c) => c.UserDetailsPageComponent),
          },
          {
            path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
            title: resolveTitleFor(Route.UserEdit),
            canActivate: [HasPermissionGuard(Route.UserEdit)],
            loadComponent: () =>
              import('./pages/user').then((c) => c.UserEditPageComponent),
          },
        ],
      },
      {
        path: RouteFragment.Orders,
        title: resolveTitleFor(Route.Orders),
        canActivate: [HasPermissionGuard(Route.Orders)],
        loadComponent: () =>
          import('./pages/orders').then((c) => c.OrderListPageComponent),
        children: [
          {
            path: `:${RouteParam.Guid}/${RouteFragment.Details}`,
            title: resolveTitleFor(Route.OrderDetails),
            canActivate: [HasPermissionGuard(Route.OrderDetails)],
            loadComponent: () =>
              import('./pages/orders').then((c) => c.OrderDetailsPageComponent),
          },
        ],
      },
      {
        path: RouteFragment.Subscriptions,
        title: resolveTitleFor(Route.Subscriptions),
        canActivate: [HasPermissionGuard(Route.Subscriptions)],
        loadComponent: () =>
          import('./pages/subscriptions').then(
            (c) => c.SubscriptionListPageComponent,
          ),
        children: [
          {
            path: `${RouteFragment.PriceChange}`,
            title: resolveTitleFor(Route.SubscriptionBulkPriceChange),
            canActivate: [
              HasPermissionGuard(Route.SubscriptionBulkPriceChange),
            ],
            loadComponent: () =>
              import('./pages/subscriptions').then(
                (c) => c.SubscriptionBulkPriceChangePageComponent,
              ),
          },
          {
            path: `:${RouteParam.Guid}/${RouteFragment.Details}`,
            title: resolveTitleFor(Route.SubscriptionDetails),
            canActivate: [HasPermissionGuard(Route.SubscriptionDetails)],
            loadComponent: () =>
              import('./pages/subscriptions').then(
                (c) => c.SubscriptionDetailsPageComponent,
              ),
          },
        ],
      },
      {
        path: RouteFragment.Invoices,
        title: resolveTitleFor(Route.Invoices),
        canActivate: [HasPermissionGuard(Route.Invoices)],
        loadComponent: () =>
          import('./pages/invoices').then((c) => c.InvoiceListPageComponent),
      },
      {
        path: RouteFragment.Brands,
        title: resolveTitleFor(Route.Brands),
        canActivate: [HasPermissionGuard(Route.Brands)],
        providers: [AdminBrandEventBusService],
        loadComponent: () =>
          import('./pages/brand').then((c) => c.BrandListPageComponent),
        children: [
          {
            path: RouteFragment.Create,
            title: resolveTitleFor(Route.BrandCreate),
            canActivate: [HasPermissionGuard(Route.BrandCreate)],
            loadComponent: () =>
              import('./pages/brand').then((c) => c.BrandCreatePageComponent),
          },
          {
            path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
            title: resolveTitleFor(Route.BrandEdit),
            canActivate: [HasPermissionGuard(Route.BrandEdit)],
            loadComponent: () =>
              import('./pages/brand').then((c) => c.BrandEditPageComponent),
          },
          {
            path: `:${RouteParam.Guid}/${RouteFragment.Details}`,
            title: resolveTitleFor(Route.BrandDetails),
            canActivate: [HasPermissionGuard(Route.BrandDetails)],
            loadComponent: () =>
              import('./pages/brand').then((c) => c.BrandDetailsPageComponent),
          },
        ],
      },
      {
        path: RouteFragment.Notifications,
        title: resolveTitleFor(Route.Notifications),
        canActivate: [HasPermissionGuard(Route.Notifications)],
        loadComponent: () => import('./pages/notifications').then((c) => c.NotificationListPageComponent),
        children: [
          {
            path: `:${RouteParam.Guid}`,
            title: resolveTitleFor(Route.NotificationDetails),
            canActivate: [HasPermissionGuard(Route.NotificationDetails)],
            loadComponent: () =>
              import('./pages/notifications').then((c) => c.NotificationDetailsPageComponent),
          },
        ],
      },
      {
        path: RouteFragment.Warehouse,
        title: resolveTitleFor(Route.Warehouse),
        canActivate: [HasPermissionGuard(Route.Warehouse)],
        children: [
          {
            path: RouteFragment.Empty,
            pathMatch: 'full',
            redirectTo: RouteFragment.Receipts,
          },
          {
            path: RouteFragment.Receipts,
            title: resolveTitleFor(Route.WarehouseReceipts),
            canActivate: [HasPermissionGuard(Route.WarehouseReceipts)],
            providers: [WarehouseReceiptEventBusService],
            loadComponent: () =>
              import('./pages/warehouse').then(
                (c) => c.WarehouseReceiptsPageComponent,
              ),
            children: [
              {
                path: RouteFragment.Create,
                title: resolveTitleFor(Route.WarehouseReceiptCreate),
                canActivate: [HasPermissionGuard(Route.WarehouseReceiptCreate)],
                loadComponent: () =>
                  import('./pages/warehouse').then(
                    (c) => c.WarehouseReceiptCreatePageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
                title: resolveTitleFor(Route.WarehouseReceiptEdit),
                canActivate: [HasPermissionGuard(Route.WarehouseReceiptEdit)],
                loadComponent: () =>
                  import('./pages/warehouse').then(
                    (c) => c.WarehouseReceiptEditPageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}`,
                title: resolveTitleFor(Route.WarehouseReceiptDetails),
                canActivate: [
                  HasPermissionGuard(Route.WarehouseReceiptDetails),
                ],
                loadComponent: () =>
                  import('./pages/warehouse').then(
                    (c) => c.WarehouseReceiptDetailsPageComponent,
                  ),
              },
            ],
          },
          {
            path: `${RouteFragment.Products}/:${RouteParam.Slug}`,
            title: resolveTitleFor(Route.WarehouseProducts),
            canActivate: [HasPermissionGuard(Route.WarehouseProducts)],
            providers: [WarehouseProductEventBusService],
            loadComponent: () =>
              import('./pages/warehouse').then(
                (c) => c.WarehouseProductsPageComponent,
              ),
            children: [
              {
                path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
                title: resolveTitleFor(Route.WarehouseProductEdit),
                canActivate: [HasPermissionGuard(Route.WarehouseProductEdit)],
                loadComponent: () =>
                  import('./pages/warehouse').then(
                    (c) => c.WarehouseProductEditPageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}`,
                title: resolveTitleFor(Route.WarehouseProductDetails),
                canActivate: [
                  HasPermissionGuard(Route.WarehouseProductDetails),
                ],
                loadComponent: () =>
                  import('./pages/warehouse').then(
                    (c) => c.WarehouseProductDetailsPageComponent,
                  ),
              },
            ],
          },
          {
            path: RouteFragment.Releases,
            title: resolveTitleFor(Route.WarehouseReleases),
            canActivate: [HasPermissionGuard(Route.WarehouseReleases)],
            providers: [WarehouseReleaseEventBusService],
            loadComponent: () =>
              import('./pages/warehouse').then(
                (c) => c.WarehouseReleasesPageComponent,
              ),
            children: [
              {
                path: RouteFragment.Create,
                title: resolveTitleFor(Route.WarehouseReleaseCreate),
                canActivate: [HasPermissionGuard(Route.WarehouseReleaseCreate)],
                loadComponent: () =>
                  import('./pages/warehouse').then(
                    (c) => c.WarehouseReleaseCreatePageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
                title: resolveTitleFor(Route.WarehouseReleaseEdit),
                canActivate: [HasPermissionGuard(Route.WarehouseReleaseEdit)],
                loadComponent: () =>
                  import('./pages/warehouse').then(
                    (c) => c.WarehouseReleaseEditPageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}`,
                title: resolveTitleFor(Route.WarehouseReleaseDetails),
                canActivate: [
                  HasPermissionGuard(Route.WarehouseReleaseDetails),
                ],
                loadComponent: () =>
                  import('./pages/warehouse').then(
                    (c) => c.WarehouseReleaseDetailsPageComponent,
                  ),
              },
            ],
          },
          {
            path: RouteFragment.Restocks,
            title: resolveTitleFor(Route.WarehouseRestocks),
            canActivate: [HasPermissionGuard(Route.WarehouseRestocks)],
            providers: [WarehouseRestockEventBusService],
            loadComponent: () =>
              import('./pages/warehouse').then(
                (c) => c.WarehouseRestocksPageComponent,
              ),
            children: [
              {
                path: RouteFragment.Create,
                title: resolveTitleFor(Route.WarehouseRestockCreate),
                canActivate: [HasPermissionGuard(Route.WarehouseRestockCreate)],
                loadComponent: () =>
                  import('./pages/warehouse').then(
                    (c) => c.WarehouseRestockCreatePageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
                title: resolveTitleFor(Route.WarehouseRestockEdit),
                canActivate: [HasPermissionGuard(Route.WarehouseRestockEdit)],
                loadComponent: () =>
                  import('./pages/warehouse').then(
                    (c) => c.WarehouseRestockEditPageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}`,
                title: resolveTitleFor(Route.WarehouseRestockDetails),
                canActivate: [
                  HasPermissionGuard(Route.WarehouseRestockDetails),
                ],
                loadComponent: () =>
                  import('./pages/warehouse').then(
                    (c) => c.WarehouseRestockDetailsPageComponent,
                  ),
              },
            ],
          },
          {
            path: RouteFragment.Owners,
            title: resolveTitleFor(Route.WarehouseOwners),
            canActivate: [HasPermissionGuard(Route.WarehouseOwners)],
            loadComponent: () =>
              import('./pages/warehouse').then(
                (c) => c.WarehouseOwnersPageComponent,
              ),
          },
          {
            path: RouteFragment.Localizations,
            providers: [WarehouseLocalizationEventBusService],
            title: resolveTitleFor(Route.WarehouseLocalizations),
            canActivate: [HasPermissionGuard(Route.WarehouseLocalizations)],
            loadComponent: () =>
              import('./pages/warehouse').then(
                (c) => c.WarehouseLocalizationsPageComponent,
              ),
            children: [
              {
                path: RouteFragment.Create,
                title: resolveTitleFor(Route.WarehouseLocalizationCreate),
                canActivate: [
                  HasPermissionGuard(Route.WarehouseLocalizationCreate),
                ],
                loadComponent: () =>
                  import('./pages/warehouse').then(
                    (c) => c.WarehouseLocalizationCreatePageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
                title: resolveTitleFor(Route.WarehouseLocalizationEdit),
                canActivate: [
                  HasPermissionGuard(Route.WarehouseLocalizationEdit),
                ],
                loadComponent: () =>
                  import('./pages/warehouse').then(
                    (c) => c.WarehouseLocalizationEditPageComponent,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: RouteFragment.Cosmetics,
        providers: [AdminCosmeticEventBusService],
        title: resolveTitleFor(Route.Cosmetics),
        canActivate: [HasPermissionGuard(Route.Cosmetics)],
        loadComponent: () =>
          import('./pages/cosmetic').then((c) => c.CosmeticListPageComponent),
        children: [
          {
            path: RouteFragment.Create,
            canActivate: [HasPermissionGuard(Route.CosmeticCreate)],
            title: resolveTitleFor(Route.CosmeticCreate),
            loadComponent: () =>
              import('./pages/cosmetic').then(
                (c) => c.CosmeticCreatePageComponent,
              ),
          },
          {
            path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
            title: resolveTitleFor(Route.CosmeticEdit),
            canActivate: [HasPermissionGuard(Route.CosmeticEdit)],
            loadComponent: () =>
              import('./pages/cosmetic').then(
                (c) => c.CosmeticEditPageComponent,
              ),
          },
          {
            path: `:${RouteParam.Guid}`,
            title: resolveTitleFor(Route.CosmeticDetails),
            canActivate: [HasPermissionGuard(Route.CosmeticDetails)],
            loadComponent: () =>
              import('./pages/cosmetic').then(
                (c) => c.CosmeticDetailsPageComponent,
              ),
          },
        ],
      },
      {
        path: RouteFragment.Boxes,
        providers: [AdminBoxEventBusService],
        title: resolveTitleFor(Route.Boxes),
        canActivate: [HasPermissionGuard(Route.Boxes)],
        loadComponent: () =>
          import('./pages/boxes').then((c) => c.BoxListPageComponent),
        children: [
          {
            path: RouteFragment.Create,
            title: resolveTitleFor(Route.BoxCreate),
            canActivate: [HasPermissionGuard(Route.BoxCreate)],
            loadComponent: () =>
              import('./pages/boxes').then((c) => c.BoxCreatePageComponent),
          },
          {
            path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
            title: resolveTitleFor(Route.BoxEdit),
            canActivate: [HasPermissionGuard(Route.BoxEdit)],
            loadComponent: () =>
              import('./pages/boxes').then((c) => c.BoxEditPageComponent),
          },
          {
            path: `:${RouteParam.Guid}/${RouteFragment.Details}`,
            title: resolveTitleFor(Route.BoxSaleDetailsFromBoxes),
            canActivate: [HasPermissionGuard(Route.BoxSaleDetailsFromBoxes)],
            loadComponent: () =>
              import('./pages/boxes').then(
                (c) => c.BoxSaleDetailsFromBoxesPageComponent,
              ),
          },
          {
            path: `:${RouteParam.Guid}`,
            title: resolveTitleFor(Route.BoxDetails),
            canActivate: [HasPermissionGuard(Route.BoxDetails)],
            loadComponent: () =>
              import('./pages/boxes').then((c) => c.BoxDetailsPageComponent),
          },
        ],
      },
      {
        path: `${RouteFragment.BoxSale}/${RouteFragment.Questionnaires}`,
        title: resolveTitleFor(Route.BoxQuestionnaires),
        canActivate: [HasPermissionGuard(Route.BoxQuestionnaires)],
        loadComponent: () =>
          import('./pages/box-questionnaire').then(
            (c) => c.BoxQuestionnaireListPageComponent,
          ),
        children: [
          {
            path: `:${RouteParam.Guid}`,
            title: resolveTitleFor(Route.BoxQuestionnaireDetails),
            canActivate: [HasPermissionGuard(Route.BoxQuestionnaireDetails)],
            loadComponent: () =>
              import('./pages/box-questionnaire').then(
                (c) => c.BoxQuestionnaireDetailsPageComponent,
              ),
          },
        ],
      },
      {
        path: RouteFragment.BoxSale,
        providers: [AdminBoxSaleEventBusService],
        title: resolveTitleFor(Route.BoxSales),
        canActivate: [HasPermissionGuard(Route.BoxSales)],
        loadComponent: () =>
          import('./pages/box-sale').then((c) => c.BoxSaleListPageComponent),
        children: [
          {
            path: `${RouteFragment.Box}/:${RouteParam.Guid}`,
            title: resolveTitleFor(Route.BoxSaleBoxDetails),
            canActivate: [HasPermissionGuard(Route.BoxSaleBoxDetails)],
            loadComponent: () =>
              import('./pages/box-sale').then(
                (c) => c.BoxSaleBoxDetailsPageComponent,
              ),
          },
          {
            path: `:${RouteParam.Guid}/${RouteFragment.Details}`,
            title: resolveTitleFor(Route.BoxSaleDetails),
            canActivate: [HasPermissionGuard(Route.BoxSaleDetails)],
            loadComponent: () =>
              import('./pages/box-sale').then(
                (c) => c.BoxSaleDetailsPageComponent,
              ),
          },
          {
            path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
            title: resolveTitleFor(Route.BoxSaleEdit),
            canActivate: [HasPermissionGuard(Route.BoxSaleEdit)],
            loadComponent: () =>
              import('./pages/box-sale').then(
                (c) => c.BoxSaleEditPageComponent,
              ),
          },
        ],
      },
      {
        path: RouteFragment.Products,
        providers: [AdminProductEventBusService],
        title: resolveTitleFor(Route.Products),
        canActivate: [HasPermissionGuard(Route.Products)],
        loadComponent: () =>
          import('./pages/product').then((c) => c.ProductListPageComponent),
        children: [
          {
            path: RouteFragment.Create,
            title: resolveTitleFor(Route.ProductCreate),
            canActivate: [HasPermissionGuard(Route.ProductCreate)],
            loadComponent: () =>
              import('./pages/product').then(
                (c) => c.ProductCreatePageComponent,
              ),
          },
          {
            path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
            title: resolveTitleFor(Route.ProductEdit),
            canActivate: [HasPermissionGuard(Route.ProductEdit)],
            loadComponent: () =>
              import('./pages/product').then((c) => c.ProductEditPageComponent),
          },
          {
            path: `:${RouteParam.Guid}`,
            title: resolveTitleFor(Route.ProductDetails),
            canActivate: [HasPermissionGuard(Route.ProductDetails)],
            loadComponent: () =>
              import('./pages/product').then(
                (c) => c.ProductDetailsPageComponent,
              ),
          },
        ],
      },
      {
        path: RouteFragment.Config,
        title: resolveTitleFor(Route.ShopConfig),
        canActivate: [HasPermissionGuard(Route.ShopConfig)],
        children: [
          {
            path: RouteFragment.Empty,
            pathMatch: 'full',
            redirectTo: RouteFragment.Banners,
          },
          {
            path: `${RouteFragment.Banners}/${RouteFragment.Home}`,
            title: resolveTitleFor(Route.ShopHomeBannerList),
            canActivate: [HasPermissionGuard(Route.ShopHomeBannerList)],
            providers: [AdminBannerEventBusService],
            loadComponent: () =>
              import('./pages/banner').then(
                (c) => c.HomeBannerListPageComponent,
              ),
            children: [
              {
                path: RouteFragment.Create,
                title: resolveTitleFor(Route.ShopHomeBannerCreate),
                canActivate: [HasPermissionGuard(Route.ShopHomeBannerCreate)],
                loadComponent: () =>
                  import('./pages/banner').then(
                    (c) => c.HomeBannerCreatePageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
                title: resolveTitleFor(Route.ShopHomeBannerEdit),
                canActivate: [HasPermissionGuard(Route.ShopHomeBannerEdit)],
                loadComponent: () =>
                  import('./pages/banner').then(
                    (c) => c.HomeBannerEditPageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}`,
                title: resolveTitleFor(Route.ShopHomeBannerDetails),
                canActivate: [HasPermissionGuard(Route.ShopHomeBannerDetails)],
                loadComponent: () =>
                  import('./pages/banner').then(
                    (c) => c.HomeBannerDetailsPageComponent,
                  ),
              },
            ],
          },
          {
            path: `${RouteFragment.Banners}/${RouteFragment.Subscribe}`,
            title: resolveTitleFor(Route.ShopSubscribeBannerList),
            canActivate: [HasPermissionGuard(Route.ShopSubscribeBannerList)],
            providers: [AdminBannerEventBusService],
            loadComponent: () =>
              import('./pages/banner').then(
                (c) => c.SubscribeBannerListPageComponent,
              ),
            children: [
              {
                path: RouteFragment.Create,
                title: resolveTitleFor(Route.ShopSubscribeBannerCreate),
                canActivate: [
                  HasPermissionGuard(Route.ShopSubscribeBannerCreate),
                ],
                loadComponent: () =>
                  import('./pages/banner').then(
                    (c) => c.SubscribeBannerCreatePageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
                title: resolveTitleFor(Route.ShopSubscribeBannerEdit),
                canActivate: [
                  HasPermissionGuard(Route.ShopSubscribeBannerEdit),
                ],
                loadComponent: () =>
                  import('./pages/banner').then(
                    (c) => c.SubscribeBannerEditPageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}`,
                title: resolveTitleFor(Route.ShopSubscribeBannerDetails),
                canActivate: [
                  HasPermissionGuard(Route.ShopSubscribeBannerDetails),
                ],
                loadComponent: () =>
                  import('./pages/banner').then(
                    (c) => c.SubscribeBannerDetailsPageComponent,
                  ),
              },
            ],
          },
          {
            path: RouteFragment.Banners,
            title: resolveTitleFor(Route.ShopBannerList),
            canActivate: [HasPermissionGuard(Route.ShopBannerList)],
            providers: [AdminBannerEventBusService],
            loadComponent: () =>
              import('./pages/banner').then((c) => c.BannerListPageComponent),
            children: [
              {
                path: RouteFragment.Create,
                title: resolveTitleFor(Route.ShopBannerCreate),
                canActivate: [HasPermissionGuard(Route.ShopBannerCreate)],
                loadComponent: () =>
                  import('./pages/banner').then(
                    (c) => c.BannerCreatePageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
                title: resolveTitleFor(Route.ShopBannerEdit),
                canActivate: [HasPermissionGuard(Route.ShopBannerEdit)],
                loadComponent: () =>
                  import('./pages/banner').then(
                    (c) => c.BannerEditPageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}`,
                title: resolveTitleFor(Route.ShopBannerDetails),
                canActivate: [HasPermissionGuard(Route.ShopBannerDetails)],
                loadComponent: () =>
                  import('./pages/banner').then(
                    (c) => c.BannerDetailsPageComponent,
                  ),
              },
            ],
          },
          {
            path: RouteFragment.ShippingMethods,
            title: resolveTitleFor(Route.ShippingMethods),
            canActivate: [HasPermissionGuard(Route.ShippingMethods)],
            providers: [AdminShippingMethodEventBusService],
            loadComponent: () =>
              import('./pages/shipping-method').then(
                (c) => c.ShippingMethodListPageComponent,
              ),
            children: [
              {
                path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
                title: resolveTitleFor(Route.ShippingMethodEdit),
                canActivate: [HasPermissionGuard(Route.ShippingMethodEdit)],
                loadComponent: () =>
                  import('./pages/shipping-method').then(
                    (c) => c.ShippingMethodEditPageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}`,
                title: resolveTitleFor(Route.ShippingMethodDetails),
                canActivate: [HasPermissionGuard(Route.ShippingMethodDetails)],
                loadComponent: () =>
                  import('./pages/shipping-method').then(
                    (c) => c.ShippingMethodDetailsPageComponent,
                  ),
              },
            ],
          },
          {
            path: RouteFragment.PaymentMethods,
            title: resolveTitleFor(Route.PaymentMethods),
            canActivate: [HasPermissionGuard(Route.PaymentMethods)],
            providers: [AdminPaymentMethodEventBusService],
            loadComponent: () =>
              import('./pages/payment-method').then(
                (c) => c.PaymentMethodListPageComponent,
              ),
            children: [
              {
                path: `:${RouteParam.Guid}/${RouteFragment.Edit}`,
                title: resolveTitleFor(Route.PaymentMethodEdit),
                canActivate: [HasPermissionGuard(Route.PaymentMethodEdit)],
                loadComponent: () =>
                  import('./pages/payment-method').then(
                    (c) => c.PaymentMethodEditPageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}`,
                title: resolveTitleFor(Route.PaymentMethodDetails),
                canActivate: [HasPermissionGuard(Route.PaymentMethodDetails)],
                loadComponent: () =>
                  import('./pages/payment-method').then(
                    (c) => c.PaymentMethodDetailsPageComponent,
                  ),
              },
            ],
          },
          {
            path: RouteFragment.KsefConfiguration,
            title: resolveTitleFor(Route.ShopKsefConfiguration),
            canActivate: [HasPermissionGuard(Route.ShopKsefConfiguration)],
            loadComponent: () =>
              import('./pages/ksef-configuration').then(
                (c) => c.KsefConfigurationPageComponent,
              ),
          },
          {
            path: RouteFragment.ShippingSettings,
            title: resolveTitleFor(Route.ShopShippingSettings),
            canActivate: [HasPermissionGuard(Route.ShopShippingSettings)],
            loadComponent: () =>
              import('./pages/shipping-settings').then(
                (c) => c.ShippingSettingsPageComponent,
              ),
          },
          {
            path: RouteFragment.RefundSettings,
            title: resolveTitleFor(Route.ShopRefundSettings),
            canActivate: [HasPermissionGuard(Route.ShopRefundSettings)],
            loadComponent: () =>
              import('./pages/refund-settings').then(
                (c) => c.RefundSettingsPageComponent,
              ),
          },
          {
            path: RouteFragment.ValidationSettings,
            title: resolveTitleFor(Route.ShopValidationSettings),
            canActivate: [HasPermissionGuard(Route.ShopValidationSettings)],
            loadComponent: () =>
              import('./pages/validation-settings').then(
                (c) => c.ValidationSettingsPageComponent,
              ),
          },
          {
            path: RouteFragment.CartSettings,
            title: resolveTitleFor(Route.ShopCartSettings),
            canActivate: [HasPermissionGuard(Route.ShopCartSettings)],
            loadComponent: () =>
              import('./pages/cart-settings').then(
                (c) => c.CartSettingsPageComponent,
              ),
          },
          {
            path: RouteFragment.BackgroundTasks,
            title: resolveTitleFor(Route.BackgroundTasks),
            canActivate: [HasPermissionGuard(Route.BackgroundTasks)],
            providers: [AdminBackgroundTaskEventBusService],
            loadComponent: () =>
              import('./pages/system').then(
                (c) => c.BackgroundTaskListPageComponent,
              ),
          },
        ],
      },
    ],
  },
  {
    path: RouteFragment.Loading,
    title: resolveTitleFor(Route.Loading),
    loadComponent: () =>
      import('./pages/loading').then((c) => c.LoadingPageComponent),
  },
  {
    path: RouteFragment.Redirect,
    loadComponent: () =>
      import('./pages/redirection').then((c) => c.RedirectionPageComponent),
  },
  {
    path: RouteFragment.SignOut,
    loadComponent: () =>
      import('./pages/auth').then((c) => c.SignOutPageComponent),
  },
  {
    path: RouteFragment.Wildcard,
    redirectTo: RouteFragment.Dashboard,
  },
];
